<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <button class="btn btn-secondary btn-back" @click="goBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchRecord">重試</button>
      </div>

      <template v-else-if="record">
        <!-- 派券摘要 -->
        <div class="detail-card">
          <div class="detail-card-header">
            <div>
              <div class="detail-title">{{ record.ticketName || `票券 ${record.ticketId}` }}</div>
              <div v-if="record.activitySubject" class="detail-subtitle">{{ record.activitySubject }}</div>
            </div>
            <span :class="statusBadgeClass(record.status)">{{ statusText(record.status) }}</span>
          </div>

          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">批次編號</span>
              <span class="detail-value cell-code">{{ record.batchId }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">票券 ID</span>
              <span class="detail-value cell-code">{{ record.ticketId }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">派券原因</span>
              <span class="detail-value">{{ record.reason || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">派發對象</span>
              <span class="detail-value">{{ record.totalTargets }} 位</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">派發結果</span>
              <span class="detail-value">
                <span class="text-success">成功 {{ record.successCount }}</span>
                <span v-if="record.failedCount > 0" class="text-danger">／失敗 {{ record.failedCount }}</span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">LINE 推播</span>
              <span class="detail-value">
                <template v-if="record.sendLineNotification">
                  已推播 {{ record.notificationSentCount }} 筆
                  <div v-if="record.lineMsgTemplateName" class="detail-sub">
                    範本：{{ record.lineMsgTemplateName }}
                  </div>
                </template>
                <template v-else>未推播</template>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">操作者</span>
              <span class="detail-value">{{ record.createdByName || record.createdByUsername || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">派券時間</span>
              <span class="detail-value">{{ formatDateTime(record.createdAt) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">執行耗時</span>
              <span class="detail-value">
                {{ record.executionDuration !== null ? `${record.executionDuration} 秒` : '-' }}
              </span>
            </div>
          </div>

          <div v-if="record.errorSummary" class="detail-error-summary">
            <i class="ri-alert-line"></i>
            <span>{{ record.errorSummary }}</span>
          </div>
        </div>

        <!-- 逐筆明細 -->
        <div class="page-toolbar">
          <div class="filter-group search-box">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="搜尋會員名稱或手機號碼..."
              @input="handleSearch"
            />
            <i class="ri-search-line"></i>
          </div>
          <div class="filter-group">
            <label class="filter-label">狀態</label>
            <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
              <option value="">全部</option>
              <option value="success">成功</option>
              <option value="failed">失敗</option>
            </select>
          </div>
        </div>

        <div v-if="detailsLoading" class="loading-state">
          <div class="spinner"></div>
          <p>載入明細中...</p>
        </div>

        <div v-else class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>對象</th>
                <th>手機</th>
                <th>來源</th>
                <th>派發結果</th>
                <th v-if="record.sendLineNotification">LINE 推播</th>
                <th>執行時間</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="details.length === 0">
                <td :colspan="record.sendLineNotification ? 6 : 5" class="empty-state">
                  沒有符合條件的明細
                </td>
              </tr>
              <tr v-for="detail in details" :key="detail.id">
                <td>{{ detail.memberName || (detail.targetSource === 'phone' ? '手動輸入' : '（會員）') }}</td>
                <td>{{ detail.memberPhone || '-' }}</td>
                <td>{{ detail.targetSource === 'phone' ? '手機直送' : 'CRM 會員' }}</td>
                <td>
                  <span :class="detail.status === 'success' ? 'badge badge-success' : 'badge badge-danger'">
                    {{ detail.status === 'success' ? '成功' : '失敗' }}
                  </span>
                  <div v-if="detail.errorMessage" class="cell-error">{{ detail.errorMessage }}</div>
                  <div v-if="detail.wixtarErrorCode" class="cell-sub">
                    Wixtar 錯誤碼：{{ detail.wixtarErrorCode }}
                  </div>
                </td>
                <td v-if="record.sendLineNotification">
                  <template v-if="detail.notificationResults?.line?.sent">
                    <span class="badge badge-success">已送出</span>
                    <div class="cell-sub">{{ formatDateTime(detail.notificationResults.line.sentAt) }}</div>
                  </template>
                  <template v-else-if="detail.notificationResults?.line">
                    <span class="badge badge-secondary">未送出</span>
                    <div class="cell-sub">{{ detail.notificationResults.line.error }}</div>
                  </template>
                  <span v-else>-</span>
                </td>
                <td>{{ formatDateTime(detail.executedAt) }}</td>
              </tr>
            </tbody>
          </table>

          <ListPagination
            v-if="pagination.totalPages > 1"
            :page="currentPage"
            :total-pages="pagination.totalPages"
            :total="pagination.totalItems"
            @change="goToPage"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { wixtarService } from '@/api/modules/wixtar.js';

/** 搜尋輸入的節流時間 */
const SEARCH_DEBOUNCE_MS = 400;

export default {
  name: 'WixtarDispatchDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle();

    const batchId = route.params.batchId;

    const record = ref(null);
    const details = ref([]);
    const loading = ref(false);
    const detailsLoading = ref(false);
    const error = ref('');
    const searchQuery = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const pagination = reactive({ totalPages: 0, totalItems: 0 });

    let searchTimer = null;

    const fetchDetails = async () => {
      detailsLoading.value = true;

      try {
        const response = await wixtarService.getDispatchRecordDetails(batchId, {
          page: currentPage.value,
          pageSize: pageSize.value,
          search: searchQuery.value.trim() || undefined,
          status: filterStatus.value || undefined
        });

        if (response?.success) {
          details.value = response.data.items || [];
          pagination.totalPages = response.data.pagination?.totalPages || 0;
          pagination.totalItems = response.data.pagination?.totalItems || 0;
        }
      } catch (err) {
        console.error('查詢派券明細失敗:', err);
      } finally {
        detailsLoading.value = false;
      }
    };

    const fetchRecord = async () => {
      loading.value = true;
      error.value = '';

      try {
        const response = await wixtarService.getDispatchRecord(batchId);

        if (response?.success) {
          record.value = response.data;
          await fetchDetails();
        } else {
          error.value = response?.message || '查詢派券紀錄失敗';
        }
      } catch (err) {
        console.error('查詢派券紀錄失敗:', err);
        error.value = err.message || '查詢派券紀錄失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        currentPage.value = 1;
        fetchDetails();
      }, SEARCH_DEBOUNCE_MS);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchDetails();
    };

    const goToPage = (page) => {
      currentPage.value = page;
      fetchDetails();
    };

    const goBack = () => {
      router.push({ name: 'ClientWixtarDispatchList' });
    };

    const formatDateTime = (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW');
    };

    const statusText = (status) => ({
      completed: '已完成',
      failed: '失敗',
      processing: '執行中'
    })[status] || status;

    const statusBadgeClass = (status) => ({
      completed: 'badge badge-success',
      failed: 'badge badge-danger',
      processing: 'badge badge-warning'
    })[status] || 'badge badge-secondary';

    onMounted(fetchRecord);

    return {
      pageTitle,
      record,
      details,
      loading,
      detailsLoading,
      error,
      searchQuery,
      filterStatus,
      currentPage,
      pagination,
      fetchRecord,
      handleSearch,
      handleFilterChange,
      goToPage,
      goBack,
      formatDateTime,
      statusText,
      statusBadgeClass
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 6 個欄位，手機版需可橫向捲動 */
.table-wrapper table {
  min-width: 900px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.detail-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 16px;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.detail-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #6c757d;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  color: #6c757d;
}

.detail-value {
  font-size: 13.5px;
  color: #333;
}

.detail-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6c757d;
}

.detail-error-summary {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 16px;
  padding: 10px 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  font-size: 12.5px;
  line-height: 1.6;
  color: #7c2d12;
}

.detail-error-summary i {
  font-size: 16px;
  flex-shrink: 0;
}

.cell-code {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 12.5px;
  color: #495057;
}

.cell-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6c757d;
}

.cell-error {
  margin-top: 4px;
  font-size: 12px;
  color: #b91c1c;
  line-height: 1.5;
}

.text-success {
  color: #15803d;
  font-weight: 600;
}

.text-danger {
  color: #b91c1c;
  font-weight: 600;
}

.loading-state,
.error-state {
  padding: 48px 20px;
  text-align: center;
  color: #6c757d;
}

.error-message {
  color: #b91c1c;
  margin-bottom: 12px;
}

.empty-state {
  padding: 32px 20px;
  text-align: center;
  color: #6c757d;
}

.spinner {
  width: 32px;
  height: 32px;
  margin: 0 auto 12px;
  border: 3px solid #e9ecef;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
